import React, { useEffect, useState } from "react";
import { httpClient } from "../../../../services/httpClient";

export default function CentroFormModal({
  open,
  eyebrow,
  title,
  icon,
  tab,
  setTab,
  form,
  setForm,
  saving,
  onClose,
  onSubmit,
  submitLabel,
  submitDisabled = false,
}) {
  const buildRemoteSrc = (path) => {
    if (!path) return "";
    const trimmed = String(path).trim();
    if (!trimmed) return "";
    if (/^https?:\/\//i.test(trimmed)) return trimmed;
    const baseUrl = httpClient?.baseUrl || "";
    if (!baseUrl) return trimmed;
    if (trimmed.startsWith("/")) return `${baseUrl}${trimmed}`;
    return `${baseUrl}/${trimmed}`;
  };

  const useImagePreview = (file, path) => {
    const [objectUrl, setObjectUrl] = useState("");
    useEffect(() => {
      if (!file) {
        setObjectUrl("");
        return undefined;
      }
      const url = URL.createObjectURL(file);
      setObjectUrl(url);
      return () => URL.revokeObjectURL(url);
    }, [file]);
    if (file) return objectUrl;
    return buildRemoteSrc(path);
  };

  const logoPreview = useImagePreview(
    form.logo_file,
    form.logo_path_url || form.logo_url || form.logo_path
  );
  const ministerioPreview = useImagePreview(
    form.ministerio_logo_file,
    form.ministerio_logo_path_url || form.ministerio_logo_url || form.ministerio_logo_path
  );
  const fondoMencionPreview = useImagePreview(
    form.fondo_mencion_file,
    form.fondo_mencion_path_url || form.fondo_mencion_url || form.fondo_mencion_path
  );
  const firmaPreview = useImagePreview(
    form.representante_firma_file,
    form.representante_firma_path_url || form.representante_firma_url || form.representante_firma_path
  );
  const headerPreview = useImagePreview(
    form.header_text_image_file,
    form.header_text_image_path_url || form.header_text_image_url || form.header_text_image_path
  );
  const footerPreview = useImagePreview(
    form.footer_image_file,
    form.footer_image_path_url || form.footer_image_url || form.footer_image_path
  );

  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState("");

  const openPreview = (label, src) => {
    setPreviewError("");
    if (src) setPreview({ src, label });
  };

  const closePreview = () => {
    setPreviewError("");
    setPreview(null);
  };

  if (!open) return null;

  const renderCentroFields = () => (
    <>
      <div className="centros-form-note text-muted small">Campos obligatorios *</div>

      <div className="centros-form-section">
        <div className="centros-form-section-header">
          <h6 className="centros-form-section-title mb-1">Datos del centro</h6>
          <p className="centros-form-section-help mb-0">Informacion principal y ubicacion.</p>
        </div>
        <div className="row g-3 mt-1">
          <div className="col-9">
            <label className="form-label dashboard-label">Nombre *</label>
            <input
              type="text"
              className="form-control"
              value={form.nombre ?? ""}
              onChange={(e) => setForm({ ...form, nombre: e.target.value })}
              placeholder="Nombre del centro"
              required
            />
          </div>
          <div className="col-3">
            <label className="form-label dashboard-label">Código MDI</label>
            <input
              type="text"
              className="form-control"
              value={form.codigo_mdi ?? ""}
              onChange={(e) => setForm({ ...form, codigo_mdi: e.target.value })}
              placeholder="Ej: CENDCAP"
            />
          </div>
          <div className="col-md-6">
            <label className="form-label dashboard-label">Alias comercial</label>
            <input
              type="text"
              className="form-control"
              value={form.alias ?? ""}
              onChange={(e) => setForm({ ...form, alias: e.target.value })}
              placeholder="Alias o nombre corto"
            />
          </div>
          <div className="col-md-6">
            <label className="form-label dashboard-label">Ciudad</label>
            <input
              type="text"
              className="form-control"
              value={form.ciudad ?? ""}
              onChange={(e) => setForm({ ...form, ciudad: e.target.value })}
              placeholder="Ciudad del centro"
            />
          </div>
        </div>
      </div>

      <div className="centros-form-section">
        <div className="centros-form-section-header">
          <h6 className="centros-form-section-title mb-1">Contacto</h6>
          <p className="centros-form-section-help mb-0">Datos para comunicarse con el centro.</p>
        </div>
        <div className="row g-3 mt-1">
          <div className="col-md-6">
            <label className="form-label dashboard-label">RUC</label>
            <input
              type="text"
              className="form-control"
              value={form.ruc ?? ""}
              onChange={(e) => setForm({ ...form, ruc: e.target.value })}
              placeholder="099xxxxxxxxx"
            />
          </div>
          <div className="col-md-6">
            <label className="form-label dashboard-label">Telefono</label>
            <input
              type="text"
              className="form-control"
              value={form.telefono ?? ""}
              onChange={(e) => setForm({ ...form, telefono: e.target.value })}
              placeholder="09xxxxxxxx"
            />
          </div>
          <div className="col-md-6">
            <label className="form-label dashboard-label">Email</label>
            <input
              type="email"
              className="form-control"
              value={form.email ?? ""}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="correo@dominio.com"
            />
          </div>
          <div className="col-md-6">
            <label className="form-label dashboard-label">Direccion</label>
            <input
              type="text"
              className="form-control"
              value={form.direccion ?? ""}
              onChange={(e) => setForm({ ...form, direccion: e.target.value })}
              placeholder="Calle y referencia"
            />
          </div>
        </div>
      </div>

      <div className="centros-form-section centros-form-section--compact">
        <div className="form-check form-switch">
          <input
            className="form-check-input"
            type="checkbox"
            id="centroActivo"
            checked={form.activo}
            onChange={(e) => setForm({ ...form, activo: e.target.checked })}
          />
          <label className="form-check-label" htmlFor="centroActivo">
            Centro activo
          </label>
        </div>
      </div>
    </>
  );

  const renderRepresentanteFields = () => (
    <div className="centros-form-section">
      <div className="centros-form-section-header">
        <h6 className="centros-form-section-title mb-1">Representante</h6>
        <p className="centros-form-section-help mb-0">Informacion del representante legal.</p>
      </div>
      <div className="row g-3 mt-1">
        <div className="col-md-6">
          <label className="form-label dashboard-label">Representante legal</label>
          <input
            type="text"
            className="form-control"
            value={form.representante_legal ?? ""}
            onChange={(e) => setForm({ ...form, representante_legal: e.target.value })}
            placeholder="Nombre completo"
          />
        </div>
        <div className="col-md-6">
          <label className="form-label dashboard-label">Profesion / grado</label>
          <input
            type="text"
            className="form-control"
            value={form.representante_profesion ?? ""}
            onChange={(e) => setForm({ ...form, representante_profesion: e.target.value })}
            placeholder="Profesion o grado"
          />
        </div>
        <div className="col-md-6">
          <label className="form-label dashboard-label">Cargo</label>
          <input
            type="text"
            className="form-control"
            value={form.representante_cargo ?? ""}
            onChange={(e) => setForm({ ...form, representante_cargo: e.target.value })}
            placeholder="Cargo en la institucion"
          />
        </div>
        <div className="col-md-6">
          <label className="form-label dashboard-label">Cedula representante</label>
          <input
            type="text"
            className="form-control"
            value={form.representante_cedula ?? ""}
            onChange={(e) => setForm({ ...form, representante_cedula: e.target.value })}
            placeholder="Documento de identidad"
          />
        </div>
      </div>
    </div>
  );

  const renderImagenFields = () => {
    const fileHint = "Formatos: PNG, JPG, WEBP, SVG";
    const buildHint = (label, pathKey, fileKey, previewSrc) => {
      const inputId = `${fileKey}-input`;
      const displayName =
        form[fileKey]?.name ||
        (form[pathKey] ? form[pathKey].split("/").pop() : "Sin archivo cargado");
      const hasPreview = Boolean(previewSrc);

      return (
        <div className="centros-image-card">
          <label className="form-label dashboard-label d-block">{label}</label>
          <small className="text-muted d-block mb-2">{fileHint}</small>
          <div className="input-group">
            <input type="text" className="form-control" value={displayName} readOnly />
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={() => document.getElementById(inputId)?.click()}
            >
              Seleccionar
            </button>
{/*             {hasPreview && (
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => openPreview(label, previewSrc)}
              >
                Ver
              </button>
            )} */}
          </div>
          <input
            id={inputId}
            type="file"
            className="d-none"
            accept=".png,.jpg,.jpeg,.webp,.svg"
            onChange={(e) =>
              setForm({
                ...form,
                [fileKey]: e.target.files?.[0] || null,
              })
            }
          />
          {hasPreview && (
            <button
              type="button"
              className="centros-image-preview"
              onClick={() => openPreview(label, previewSrc)}
            >
              <img src={previewSrc} alt={label} />
            </button>
          )}
        </div>
      );
    };

    return (
      <div className="centros-image-grid">
        {buildHint("Logo institucional", "logo_path", "logo_file", logoPreview)}
        {buildHint("Logo Ministerio del Interior", "ministerio_logo_path", "ministerio_logo_file", ministerioPreview)}
        {buildHint("Fondo menciones de honor", "fondo_mencion_path", "fondo_mencion_file", fondoMencionPreview)}
        {buildHint("Firma representante", "representante_firma_path", "representante_firma_file", firmaPreview)}
        {buildHint(
          "Imagen header (texto/logo)",
          "header_text_image_path",
          "header_text_image_file",
          headerPreview
        )}
        {buildHint("Imagen footer", "footer_image_path", "footer_image_file", footerPreview)}
      </div>
    );
  };

  return (
    <div className="modal fade show d-block management-modal centros-modal" tabIndex="-1" role="dialog">
      <div
        className="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-lg"
        role="document"
      >
        <div className="modal-content">
          <div className="modal-header border-0">
            <div>
              <p className="dashboard-eyebrow mb-1">{eyebrow}</p>
              <h5 className="modal-title mb-0 d-flex align-items-center gap-2">
                <i className={icon}></i> {title}
              </h5>
            </div>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>
          <form onSubmit={onSubmit}>
            <div className="modal-body">
              <ul className="nav nav-tabs mb-3">
                <li className="nav-item">
                  <button
                    type="button"
                    className={`nav-link ${tab === "centro" ? "active" : ""}`}
                    onClick={() => setTab("centro")}
                  >
                    Centro
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    type="button"
                    className={`nav-link ${tab === "representante" ? "active" : ""}`}
                    onClick={() => setTab("representante")}
                  >
                    Representante
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    type="button"
                    className={`nav-link ${tab === "imagenes" ? "active" : ""}`}
                    onClick={() => setTab("imagenes")}
                  >
                    Imagenes
                  </button>
                </li>
              </ul>

              {tab === "centro" && renderCentroFields()}
              {tab === "representante" && renderRepresentanteFields()}
              {tab === "imagenes" && renderImagenFields()}
            </div>
            <div className="modal-footer border-0 pt-0">
              <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
                Cancelar
              </button>
              <button
                type="submit"
                className="btn management-btn-primary"
                disabled={saving || submitDisabled}
              >
                {saving ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Guardando...
                  </>
                ) : (
                  submitLabel
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
      {preview && (
        <div
          className="modal fade show d-block management-modal centros-preview-modal"
          tabIndex="-1"
          role="dialog"
        >
          <div className="modal-dialog modal-dialog-centered modal-md" role="document">
            <div className="modal-content">
              <div className="modal-header border-0">
                <h6 className="modal-title mb-0">{preview.label}</h6>
                <button type="button" className="btn-close" onClick={closePreview}></button>
              </div>
              <div className="modal-body">
                {previewError ? (
                  <div className="text-muted small">{previewError}</div>
                ) : (
                  <img
                    src={preview.src}
                    alt={preview.label}
                    className="centros-preview-image"
                    onError={() => setPreviewError("No se pudo cargar la imagen.")}
                  />
                )}
              </div>
              <div className="modal-footer border-0 pt-0">
                <button type="button" className="btn btn-outline-secondary" onClick={closePreview}>
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
